import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "../../ui/UIElements/Icon";
import { Button } from "../../ui/UIElements/Button";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import { useConversation } from "@finitless/shared/hooks";
import ConversationChat from "../../ui/Chat/ConversationChat";
import { convertTime, useGlobalUtils } from "../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import "./conversations-pages.css";

const ConversationDetailPage = ({ initialConversation = null }) => {
  const { t } = useTranslation("conversations");
  const { id } = useParams();
  const conversationId = parseInt(id, 10);
  const navigate = useNavigate();
  const { parseDate, parsePrice } = useGlobalUtils();
  const {
    data: fetchedConversation,
    isLoading: conversationLoading,
    error: conversationError,
  } = useConversation(conversationId, {
    params:
      "id,status,status_reason,type,user(name,lastname,country_phone_code,cellphone),agent,messages_count,user_email,user_cellphone,last_message_at,created_at,public_data,private_data,order(summary),usage",
  });
  const conversation = fetchedConversation || initialConversation;

  const handleBackToConversations = () => navigate("/analyze/conversations");
  const handleViewOrder = () => {
    if (!conversation?.order?.id) return;
    navigate(`/orders/${conversation.order.id}`);
  };

  const formatTimestamp = (timestamp) =>
    parseDate(timestamp, {
      formatTime: "12",
      outputFormat: "MMM d, h:mm a",
      fallbackText: t("chat.fallbackNoTime"),
    });

  const getStatusStyle = (status) => {
    switch (status) {
      case "active":
        return "border-success/40 bg-success-bg text-success";
      case "closed":
        return "border-border bg-muted text-muted-foreground";
      case "writing":
        return "border-primary/40 bg-accent text-primary";
      default:
        return "border-border bg-muted text-muted-foreground";
    }
  };

  const getStatusReasonStyle = (status_reason) => {
    switch (status_reason) {
      case "completed":
        return "border-success/40 bg-success-bg text-success";
      case "abandoned":
        return "border-warning/40 bg-warning-bg text-warning";
      case "failed":
        return "border-error/40 bg-error-bg text-error";
      default:
        return "border-border bg-muted text-muted-foreground";
    }
  };

  if (conversationLoading) {
    return (
      <div
        className="border-border bg-card flex min-h-96 items-center justify-center rounded-lg border shadow-[var(--od-shadow-1)]"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="border-muted border-b-primary mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 motion-reduce:animate-none"></div>
          <p className="text-muted-foreground">{t("detail.states.loading")}</p>
        </div>
      </div>
    );
  }

  if (conversationError || (!conversationLoading && !conversation)) {
    return (
      <div
        className="border-border bg-card rounded-lg border px-4 py-12 text-center shadow-[var(--od-shadow-1)]"
        role="alert"
      >
        <Icon
          name="MessageCircle"
          className="text-muted-foreground mx-auto mb-4 h-12 w-12"
        />
        <h3 className="text-foreground mt-2 text-sm font-medium">
          {t("detail.states.notFoundTitle")}
        </h3>
        <p className="text-muted-foreground mt-1 text-sm break-words">
          {conversationError?.message || t("detail.states.notFoundDescription")}
        </p>
        <Button
          variant="ghost"
          type="button"
          onClick={handleBackToConversations}
          className="bg-primary text-primary-foreground focus-visible:ring-ring mt-4 min-h-11 rounded-md px-4 py-2 font-medium transition-colors hover:bg-[var(--od-action-hover)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
        >
          {t("detail.actions.backToConversations")}
        </Button>
      </div>
    );
  }

  const userName = [conversation?.user?.name, conversation?.user?.lastname]
    .filter(Boolean)
    .join(" ")
    .trim();
  const countryPhoneCode = conversation?.user?.country_phone_code
    ? `+${conversation?.user?.country_phone_code}`
    : "";
  const userContact = [countryPhoneCode, conversation?.user?.cellphone]
    .filter(Boolean)
    .join(" ")
    .trim();
  const {
    type: conversationType,
    status,
    messages_count: messagesCount,
    last_message_at: lastMessageAt,
  } = conversation || {};
  const statusLabel = t(`status.${status}`, {
    defaultValue: status || t("detail.labels.unknown"),
  });
  const statusReasonLabel = t(`statusReason.${conversation?.status_reason}`, {
    defaultValue: conversation?.status_reason,
  });
  const formattedLastMessage = formatTimestamp(lastMessageAt);
  const formattedStartedAt = formatTimestamp(conversation?.created_at);

  const breadcrumbItems = [
    {
      label: t("header.title"),
      path: "/analyze/conversations",
      icon: "MessageCircle",
    },
    {
      label: t("detail.header.conversationNumber", { id: conversationId }),
      active: true,
      disabled: true,
    },
  ];

  const channelTypeMap = {
    whatsapp: "whatsapp",
    "whatsapp:chat": "whatsapp",
    "ordering:chat": "chat",
    "finitless:chat": "chat",
    ordering: "chat",
  };

  const getChannelData = (type) => {
    const channel = channelTypeMap[type] || "default";

    if (channel === "whatsapp") {
      return {
        label: t("detail.channels.whatsapp"),
        bgClass: "bg-success text-success-foreground",
      };
    }

    if (channel === "chat") {
      return {
        label: t("detail.channels.webChat"),
        bgClass: "bg-primary text-primary-foreground",
      };
    }

    return {
      label: t("detail.channels.web"),
      bgClass: "bg-secondary text-secondary-foreground",
    };
  };

  const channelData = getChannelData(conversationType);

  const getConversationIcon = (type) => {
    const channel = channelTypeMap[type] || "default";

    switch (channel) {
      case "whatsapp":
        return (
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${channelData.bgClass}`}
          >
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
          </div>
        );
      case "chat":
        return (
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${channelData.bgClass}`}
          >
            <Icon name="MessageCircle" className="h-6 w-6" />
          </div>
        );
      default:
        return (
          <div className="bg-secondary text-secondary-foreground flex h-12 w-12 items-center justify-center rounded-lg">
            <Icon name="Monitor" className="h-6 w-6" />
          </div>
        );
    }
  };

  const contextRows = [
    {
      label: t("detail.labels.participant"),
      value: userName || t("notifications.unknownUser"),
    },
    {
      label: t("detail.labels.contact"),
      value:
        userContact || conversation?.user_email || t("detail.labels.unknown"),
    },
    {
      label: t("detail.labels.conversationId"),
      value: conversationId,
    },
    conversation?.order?.id && {
      label: t("detail.labels.orderId"),
      value: conversation.order.id,
    },
  ].filter(Boolean);

  return (
    <div
      className="ordering-conversation-detail space-y-4 sm:space-y-6"
      data-ordering-surface="conversation-detail"
    >
      <Breadcrumb items={breadcrumbItems} />

      <div className="border-border bg-card rounded-lg border shadow-[var(--od-shadow-1)]">
        <div className="border-border border-b p-4 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div
                className="flex-shrink-0"
                data-conversation-channel={
                  channelTypeMap[conversationType] || "default"
                }
              >
                {getConversationIcon(conversationType)}
              </div>

              <div className="min-w-0">
                <p className="text-muted-foreground text-sm">
                  {t("detail.header.conversationNumber", {
                    id: conversationId,
                  })}
                </p>
                <h1 className="text-foreground text-2xl font-bold tracking-tight break-words">
                  {userName || t("notifications.unknownUser")}
                </h1>
                {userContact && (
                  <p className="text-muted-foreground text-sm break-all">
                    {userContact}
                  </p>
                )}

                {!userContact && conversation?.user_email && (
                  <p className="text-muted-foreground text-sm break-all">
                    {conversation.user_email}
                  </p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="border-border bg-muted text-foreground rounded-md border px-3 py-1 text-xs font-medium">
                    {channelData.label}
                  </span>

                  <span className="border-border bg-muted text-foreground rounded-md border px-3 py-1 text-xs font-medium">
                    {t("detail.labels.totalMessages")}: {messagesCount || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:items-end">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleBackToConversations}
                  className="border-border bg-background text-foreground hover:bg-accent focus-visible:ring-ring inline-flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                >
                  <Icon name="ArrowLeft" className="h-4 w-4 rtl:rotate-180" />
                  {t("detail.actions.backToConversations")}
                </Button>

                {!!conversation?.order?.id && (
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={handleViewOrder}
                    className="bg-primary text-primary-foreground focus-visible:ring-ring inline-flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-[var(--od-action-hover)] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                  >
                    <Icon name="ShoppingBag" className="h-4 w-4" />
                    {t("detail.actions.viewOrder")}
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <span className="text-muted-foreground text-sm">
                  {t("detail.labels.status")}:
                </span>
                <span
                  className={`rounded-md border px-3 py-1 text-sm font-medium capitalize ${getStatusStyle(status)}`}
                >
                  {statusLabel}
                </span>
              </div>

              {conversation?.status_reason && (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-muted-foreground text-sm">
                    {t("detail.labels.reason")}:
                  </span>
                  <span
                    className={`rounded-md border px-3 py-1 text-sm font-medium capitalize ${getStatusReasonStyle(conversation.status_reason)}`}
                  >
                    {statusReasonLabel}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {conversation?.status_reason === "completed" &&
              conversation?.usage?.roi != null &&
              conversation.usage.roi >= 0 && (
                <div className="border-border bg-muted rounded-md border p-3">
                  <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    {t("card.labels.roi")}
                  </p>
                  <p className="text-foreground mt-1 text-sm font-semibold break-words">
                    {t("card.labels.roiValue", {
                      value: conversation.usage.roi.toFixed(2),
                    })}
                  </p>
                </div>
              )}
            {conversation?.usage?.cost != null &&
              conversation.usage.cost >= 0 && (
                <div className="border-border bg-muted rounded-md border p-3">
                  <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    {t("detail.labels.cost")}
                  </p>
                  <p className="text-foreground mt-1 text-sm font-semibold break-words">
                    {parsePrice(conversation.usage.cost)}
                  </p>
                </div>
              )}
            {!!conversation?.usage?.average_response_time_ms && (
              <div className="border-border bg-muted rounded-md border p-3">
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  {t("card.labels.responseTime")}
                </p>
                <p className="text-foreground mt-1 text-sm font-semibold break-words">
                  {convertTime(
                    conversation.usage.average_response_time_ms,
                    "ms",
                    "s",
                    { decimals: 1, includeUnit: true },
                  )}
                </p>
              </div>
            )}
            <div className="border-border bg-muted rounded-md border p-3">
              <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {t("detail.labels.startedAt")}
              </p>
              <p className="text-foreground mt-1 text-sm font-semibold break-words">
                {formattedStartedAt}
              </p>
            </div>
            <div className="border-border bg-muted rounded-md border p-3">
              <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {t("detail.labels.lastMessage")}
              </p>
              <p className="text-foreground mt-1 text-sm font-semibold break-words">
                {formattedLastMessage}
              </p>
            </div>
          </div>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-6 p-4 sm:p-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <section className="border-border bg-background min-w-0 rounded-lg border">
            <div className="border-border flex items-center justify-between border-b px-4 py-3 sm:px-5">
              <h3 className="text-foreground text-lg font-semibold">
                {t("detail.sections.messages")}
              </h3>
              <span className="border-border bg-muted text-foreground rounded-md border px-2 py-1 text-xs font-medium">
                {messagesCount || 0}
              </span>
            </div>

            <div className="p-3 sm:p-4">
              <ConversationChat
                conversationId={conversationId}
                maxHeight="h-[62vh]"
                showScrollButton={true}
                enablePagination={true}
              />
            </div>
          </section>

          <aside className="min-w-0 space-y-4">
            <div className="border-border bg-background rounded-lg border p-4 sm:p-5">
              <h4 className="text-foreground text-sm font-semibold tracking-wide uppercase">
                {t("detail.sections.context")}
              </h4>

              <dl className="mt-4 space-y-3">
                {contextRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex min-w-0 flex-wrap items-start justify-between gap-3"
                  >
                    <dt className="text-muted-foreground text-xs">
                      {row.label}
                    </dt>
                    <dd className="text-foreground max-w-full text-end text-sm font-medium break-words">
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {conversation?.summary && (
              <div className="border-border bg-background rounded-lg border p-4 sm:p-5">
                <h4 className="text-foreground text-sm font-semibold tracking-wide uppercase">
                  {t("detail.sections.summary")}
                </h4>
                <p className="text-foreground mt-3 text-sm leading-relaxed break-words">
                  {conversation.summary}
                </p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetailPage;
